<template>
  <div class="min-h-screen" :style="pageStyle">
    <div v-if="loading" class="flex min-h-screen items-center justify-center">
      <div class="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-[var(--accent)]"></div>
    </div>

    <div v-else-if="error" class="flex min-h-screen items-center justify-center px-4">
      <div class="max-w-md space-y-3 text-center">
        <h1 class="text-3xl font-semibold text-gray-900">Status page not found</h1>
        <p class="text-gray-600">The requested status page could not be found.</p>
      </div>
    </div>

    <div v-else-if="statusPage" class="mx-auto max-w-6xl space-y-10 px-4 py-10">
      <header class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div class="flex items-center gap-3">
          <div class="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl border-2 border-gray-900 bg-white shadow-sm">
            <img
              v-if="statusPage.branding?.logoUrl"
              :src="statusPage.branding.logoUrl"
              :alt="statusPage.name"
              class="h-full w-full object-contain p-2"
            />
            <span v-else class="text-lg font-bold text-gray-900">{{ statusPage.name?.slice(0, 2)?.toUpperCase() }}</span>
          </div>
          <div>
            <p class="text-xs uppercase tracking-[0.2em] text-gray-500">Status</p>
            <h1 class="text-3xl font-bold leading-tight text-gray-900">{{ statusPage.name }}</h1>
            <p v-if="statusPage.description" class="mt-1 max-w-2xl text-sm text-gray-600">{{ statusPage.description }}</p>
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-3">
          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-full border-2 border-gray-900 bg-white px-4 py-2 text-sm font-semibold shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg"
            @click="scrollToSubscribe"
          >
            <span class="inline-block h-2 w-2 rounded-full bg-[var(--accent)]"></span>
            Subscribe
          </button>
    <!--       <div class="flex items-center gap-2 rounded-full border border-gray-200 bg-white/70 px-3 py-2 text-xs text-gray-600">
            <span class="inline-block h-2 w-2 rounded-full bg-emerald-500"></span>
            Last updated {{ formatDate(statusPage.lastUpdated) }}
          </div> -->
        </div>
      </header>

      <section class="space-y-6">
        <div class="flex flex-col items-center gap-4 rounded-2xl border-2 border-gray-900 bg-white/90 p-8 text-center shadow-lg">
          <div class="relative">
            <div class="absolute inset-0 animate-ping rounded-full opacity-30" :style="{ backgroundColor: overallStatusMeta.bg }"></div>
            <div class="relative flex h-16 w-16 items-center justify-center rounded-full text-white" :style="{ backgroundColor: overallStatusMeta.bg }">
              <svg viewBox="0 0 24 24" class="h-8 w-8">
                <path fill="currentColor" d="M9.75 16.5 5.5 12.25l1.5-1.5 2.75 2.75 7.25-7.25 1.5 1.5Z" />
              </svg>
            </div>
          </div>
          <div>
            <p class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">Current status</p>
            <h2 class="text-3xl font-semibold text-gray-900">{{ overallStatusMeta.label }}</h2>
            <p class="text-sm text-gray-600">{{ overallStatusMeta.description }}</p>
            <!-- <p class="mt-1 text-xs text-gray-500">Last updated {{ formatDate(statusPage.lastUpdated) }}</p> -->
          </div>
        </div>

        <div class="grid gap-6 md:grid-cols-[2fr,1fr]">
          <div class="rounded-2xl border-2 border-dashed border-gray-900 bg-white/80 p-5 shadow-sm">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">Availability trend</p>
                <p class="text-sm text-gray-600">Last {{ historyDays }} days</p>
              </div>
              <span class="rounded-full bg-[var(--accent)]/10 px-3 py-1 text-xs font-semibold text-[var(--accent)]">Live</span>
            </div>
            <div class="mt-4 flex h-28 items-end gap-1">
              <div
                v-for="(point, idx) in overallHistory"
                :key="`${point.date}-${idx}`"
                class="h-full flex-1 rounded-full transition-transform hover:-translate-y-1"
                :class="historyBarClass(point.uptime, point.incidents)"
                :style="{ height: `${Math.max(point.uptime * 100, 6)}%` }"
                :title="`${formatDate(point.date)} • ${(point.uptime * 100).toFixed(2)}% uptime`"
              ></div>
              <div v-if="historyLoading" class="flex h-full flex-1 items-center justify-center rounded bg-gray-100 text-xs text-gray-500">
                Loading…
              </div>
            </div>
            <div class="mt-3 flex items-center justify-between text-xs text-gray-500">
              <span>{{ historyRangeLabel.start }}</span>
              <span>{{ historyRangeLabel.end }}</span>
            </div>
            <p v-if="historyError" class="mt-2 text-xs font-semibold text-red-600">Unable to load history right now.</p>
          </div>

          <div class="rounded-2xl border-2 border-gray-900 bg-white/90 p-5 shadow-lg">
            <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-600">Snapshot</h3>
            <div class="mt-4 space-y-3">
              <div class="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50/70 px-3 py-2">
                <span class="text-sm text-gray-600">Availability (avg)</span>
                <span class="text-base font-semibold text-gray-900">
                  {{ averageUptime !== null ? `${averageUptime.toFixed(2)}%` : '—' }}
                </span>
              </div>
              <div class="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50/70 px-3 py-2">
                <span class="text-sm text-gray-600">Components operational</span>
                <span class="text-base font-semibold text-gray-900">{{ componentHealth.healthy }}/{{ componentHealth.total }}</span>
              </div>
              <div class="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50/70 px-3 py-2">
                <span class="text-sm text-gray-600">Active incidents</span>
                <span class="text-base font-semibold text-gray-900">{{ statusPage.activeIncidents?.length ?? 0 }}</span>
              </div>
              <div class="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50/70 px-3 py-2">
                <span class="text-sm text-gray-600">Scheduled maintenance</span>
                <span class="text-base font-semibold text-gray-900">{{ statusPage.scheduledMaintenances?.length ?? 0 }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section v-if="statusPage.branding?.customHtml" class="rounded-2xl border border-gray-200 bg-white/80 p-6 shadow-sm">
        <div v-html="statusPage.branding.customHtml" class="prose max-w-none"></div>
      </section>

      <section class="space-y-4" id="components">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-2xl font-semibold text-gray-900">Service status</h2>
            <p class="text-sm text-gray-600">Component health with {{ historyDays }}-day history</p>
          </div>
          <span class="rounded-full bg-[var(--accent)]/10 px-3 py-1 text-xs font-semibold text-[var(--accent)]">
            {{ componentHealth.total }} services
          </span>
        </div>

        <div v-if="sortedComponents.length === 0" class="rounded-xl border-2 border-dashed border-gray-200 bg-white/70 px-6 py-10 text-center text-gray-500">
          No services are being monitored yet.
        </div>

        <div v-else class="space-y-4">
          <article
            v-for="component in sortedComponents"
            :key="component.id"
            class="rounded-2xl border border-gray-200 bg-white/90 p-5 shadow-sm"
          >
            <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div class="flex items-center gap-2">
                  <h3 class="text-lg font-semibold text-gray-900">{{ component.name }}</h3>
                  <span
                    class="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold"
                    :class="componentStatusClass(component.status)"
                  >
                    <span class="h-2 w-2 rounded-full" :class="componentStatusDot(component.status)"></span>
                    {{ componentStatusText(component.status) }}
                  </span>
                </div>
                <p v-if="component.description" class="mt-1 max-w-3xl text-sm text-gray-600">
                  {{ component.description }}
                </p>
              </div>
              <div class="flex flex-wrap gap-4 text-xs text-gray-600">
                <div v-if="component.stats" class="flex items-center gap-1">
                  <span class="font-semibold text-gray-900">{{ (component.stats.uptime7d * 100).toFixed(2) }}%</span>
                  <span>uptime (7d)</span>
                </div>
                <div v-if="component.stats" class="flex items-center gap-1">
                  <span class="font-semibold text-gray-900">{{ component.stats.avgResponseTime24h.toFixed(0) }}ms</span>
                  <span>avg response</span>
                </div>
                <div v-if="component.stats" class="flex items-center gap-1">
                  <span class="font-semibold text-gray-900">{{ component.stats.incidentCount30d }}</span>
                  <span>incidents (30d)</span>
                </div>
              </div>
            </div>

            <div class="mt-4">
              <div class="flex h-16 items-end gap-1 rounded-xl bg-gray-50/80 p-3">
                <div
                  v-for="(point, idx) in getComponentHistory(component.id)"
                  :key="`${component.id}-${idx}`"
                  class="flex-1 rounded-full transition-transform hover:-translate-y-1"
                  :class="historyBarClass(point.uptime, point.incidents)"
                  :style="{ height: `${Math.max(point.uptime * 100, 6)}%` }"
                  :title="`${formatDate(point.date)} • ${(point.uptime * 100).toFixed(2)}% uptime${point.incidents ? ` • ${point.incidents} incident(s)` : ''}`"
                ></div>
                <div v-if="historyLoading" class="flex-1 text-center text-xs text-gray-500">…</div>
              </div>
              <div class="mt-2 flex items-center justify-between text-[11px] text-gray-500">
                <span>{{ historyRangeLabel.start }}</span>
                <span>{{ historyRangeLabel.end }}</span>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section v-if="statusPage.activeIncidents?.length" class="space-y-4" id="incidents">
        <div class="flex items-center justify-between">
          <h2 class="text-2xl font-semibold text-gray-900">Active incidents</h2>
          <span class="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
            {{ statusPage.activeIncidents.length }} open
          </span>
        </div>

        <div class="space-y-4">
          <article
            v-for="incident in statusPage.activeIncidents"
            :key="incident.id"
            class="rounded-2xl border-2 border-red-200 bg-red-50 p-5 shadow-sm"
          >
            <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div class="flex items-center gap-2">
                  <span class="inline-flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-700">
                    !
                  </span>
                  <div>
                    <h3 class="text-lg font-semibold text-red-900">{{ incident.title }}</h3>
                    <p class="text-xs uppercase tracking-wide text-red-700">{{ incident.severity }} impact</p>
                  </div>
                </div>
                <p v-if="incident.description" class="mt-2 text-sm text-red-800">
                  {{ incident.description }}
                </p>
                <p class="mt-1 text-xs text-red-700">
                  Started {{ formatDate(incident.createdAt) }} • {{ incident.status?.toString().replace('_', ' ') }}
                </p>
              </div>
            </div>

            <div v-if="incident.updates?.length" class="mt-4 space-y-3 border-l-4 border-red-200 pl-4">
              <div
                v-for="update in incident.updates.slice(0, 3)"
                :key="update.id"
                class="rounded-lg bg-white/60 px-3 py-2 shadow-sm"
              >
                <div class="flex items-center justify-between text-xs text-red-700">
                  <span class="font-semibold">{{ formatDate(update.createdAt) }}</span>
                  <span v-if="update.status" class="capitalize">{{ formatStatusLabel(update.status) }}</span>
                </div>
                <p class="mt-1 text-sm text-red-900">{{ update.message }}</p>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section v-if="statusPage.scheduledMaintenances?.length" class="space-y-4" id="maintenance">
        <div class="flex items-center justify-between">
          <h2 class="text-2xl font-semibold text-gray-900">Scheduled maintenance</h2>
          <span class="rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
            {{ statusPage.scheduledMaintenances.length }} planned
          </span>
        </div>

        <div class="space-y-3">
          <article
            v-for="maintenance in statusPage.scheduledMaintenances"
            :key="maintenance.id"
            class="rounded-2xl border-2 border-indigo-200 bg-indigo-50 p-5 shadow-sm"
          >
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold text-indigo-900">{{ maintenance.title }}</h3>
                <p v-if="maintenance.description" class="mt-1 text-sm text-indigo-800">
                  {{ maintenance.description }}
                </p>
              </div>
              <span class="rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
                {{ maintenance.status?.toString().replace('_', ' ') }}
              </span>
            </div>
            <p class="mt-2 text-sm text-indigo-700">
              {{ formatDate(maintenance.startTime) }} — {{ formatDate(maintenance.endTime) }}
            </p>
          </article>
        </div>
      </section>

      <section class="space-y-4" id="history">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-2xl font-semibold text-gray-900">Past incidents</h2>
            <p class="text-sm text-gray-600">Recent history pulled automatically</p>
          </div>
          <button
            v-if="pastIncidentsHasMore"
            class="rounded-full border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-700 transition hover:-translate-y-0.5 hover:shadow"
            :disabled="pastIncidentsLoading"
            @click="loadMorePastIncidents"
          >
            {{ pastIncidentsLoading ? 'Loading…' : 'Load more' }}
          </button>
        </div>

        <div v-if="pastIncidentsLoading && pastIncidents.length === 0" class="rounded-xl border border-gray-200 bg-white/70 px-6 py-6 text-center text-gray-500">
          Loading incident history…
        </div>

        <div v-else-if="pastIncidents.length === 0" class="rounded-xl border border-dashed border-gray-200 bg-white/70 px-6 py-10 text-center text-gray-500">
          No incident was recorded in the recent period.
        </div>

        <div v-else class="space-y-3">
          <article
            v-for="incident in pastIncidents"
            :key="incident.id"
            class="rounded-2xl border border-gray-200 bg-white/90 p-5 shadow-sm"
          >
            <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div class="flex items-center gap-2">
                  <div class="h-3 w-3 rounded-full" :class="incidentSeverityDot(incident.severity)"></div>
                  <h3 class="text-lg font-semibold text-gray-900">{{ incident.title }}</h3>
                </div>
                <p v-if="incident.description" class="mt-1 text-sm text-gray-700">{{ incident.description }}</p>
                <p class="text-xs text-gray-500">
                  {{ formatDate(incident.createdAt) }}
                  <span v-if="incident.resolvedAt"> • Resolved {{ formatDate(incident.resolvedAt) }}</span>
                </p>
              </div>
              <span class="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 capitalize">
                {{ incident.status?.toString().replace('_', ' ') }}
              </span>
            </div>

            <div v-if="incident.updates?.length" class="mt-3 space-y-2 border-l-4 border-gray-100 pl-4">
              <div
                v-for="update in incident.updates.slice(0, 3)"
                :key="update.id"
                class="rounded-lg bg-gray-50 px-3 py-2"
              >
                <div class="flex items-center justify-between text-[11px] text-gray-600">
                  <span class="font-semibold text-gray-800">{{ formatDate(update.createdAt) }}</span>
                  <span v-if="update.status" class="capitalize text-gray-600">{{ formatStatusLabel(update.status) }}</span>
                </div>
                <p class="mt-1 text-sm text-gray-700">{{ update.message }}</p>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section ref="subscriptionSection" class="rounded-2xl border-2 border-gray-900 bg-white/90 p-6 shadow-lg">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 class="text-2xl font-semibold text-gray-900">Get notified</h2>
            <p class="text-sm text-gray-600">Subscribe to incidents and maintenance updates.</p>
          </div>
          <form class="flex w-full max-w-md gap-2" @submit.prevent="subscribe">
            <input
              v-model="subscriptionEmail"
              type="email"
              required
              placeholder="you@example.com"
              class="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm shadow-sm focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30"
            />
            <!-- Honeypot field to deter bots -->
            <input v-model="honeypot" type="text" tabindex="-1" autocomplete="off" class="hidden" aria-hidden="true" />
            <button
              type="submit"
              :disabled="subscribing"
              class="whitespace-nowrap rounded-xl bg-[var(--accent)] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60"
            >
              {{ subscribing ? 'Subscribing…' : 'Subscribe' }}
            </button>
          </form>
        </div>
      </section>

      <footer class="pb-6 pt-4 text-center text-xs text-gray-500">
        <p>Monitoring powered by PingAlert</p>
      </footer>
    </div>

    <component
      v-if="statusPage?.branding?.customCss"
      :is="'style'"
      v-html="statusPage.branding.customCss"
    />

    <!-- Password Prompt -->
    <div v-if="showPasswordPrompt" class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div class="w-full max-w-md space-y-4 rounded-2xl bg-white p-6 shadow-xl">
        <div class="flex items-center justify-between">
          <h3 class="text-lg font-semibold text-gray-900">Private status page</h3>
          <button @click="showPasswordPrompt = false" class="text-gray-500 hover:text-gray-700">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <p class="text-sm text-gray-600">Enter the password to view this status page.</p>
        <form @submit.prevent="authenticate" class="space-y-3">
          <input
            v-model="password"
            type="password"
            required
            placeholder="Password"
            class="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm shadow-sm focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30"
            :class="{ 'border-red-400 focus:ring-red-200': passwordError }"
          />
          <p v-if="passwordError" class="text-sm text-red-600">{{ passwordError }}</p>
          <div class="flex justify-end gap-2">
            <button type="button" class="rounded-xl border border-gray-200 px-4 py-2 text-sm text-gray-700" @click="showPasswordPrompt = false">
              Cancel
            </button>
            <button type="submit" :disabled="authenticating" class="rounded-xl bg-[var(--accent)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {{ authenticating ? 'Authenticating…' : 'Access' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

const historyDays = 90
const route = useRoute()

const statusPage = ref<any>(null)
const historyData = ref<any>(null)
const loading = ref(true)
const historyLoading = ref(false)
const historyError = ref(false)
const error = ref(false)
const subscriptionEmail = ref('')
const subscribing = ref(false)
const subscriptionSection = ref<HTMLElement | null>(null)
const pageSessionToken = ref<string | null>(null)
const subscriptionToken = ref<string | null>(null)
const honeypot = ref('')
const pageLoadStartedAt = Date.now()
//const subscriptionToken = ref<string | null>(null)

const pastIncidents = ref<any[]>([])
const pastIncidentsLoading = ref(false)
const pastIncidentsPage = ref(1)
const pastIncidentsHasMore = ref(true)

const showPasswordPrompt = ref(false)
const password = ref('')
const passwordError = ref('')
const authenticating = ref(false)

const pageStyle = computed(() => {
  const branding = statusPage.value?.branding
  return {
    backgroundColor: branding?.backgroundColor || '#FFFFFF',
    color: branding?.textColor || '#0f172a',
    '--accent': branding?.primaryColor || '#0f172a',
    '--muted': '#6b7280',
    '--card': 'rgba(255,255,255,0.9)'
  } as Record<string, string>
})

const sortedComponents = computed(() => {
  if (!statusPage.value?.components) return []
  return [...statusPage.value.components].sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
})

const overallStatusMeta = computed(() => {
  const status = statusPage.value?.overallStatus
  const map: Record<number, { label: string; description: string; bg: string }> = {
    1: { label: 'All systems operational', description: 'Everything is running smoothly.', bg: '#16a34a' },
    2: { label: 'Minor issues', description: 'Some services may be degraded.', bg: '#fbbf24' },
    3: { label: 'Major issues', description: 'Service disruption detected.', bg: '#f97316' },
    4: { label: 'Critical outage', description: 'Multiple systems are impacted.', bg: '#ef4444' },
    5: { label: 'Under maintenance', description: 'Scheduled work is in progress.', bg: '#6366f1' }
  }
  return map[status as number] ?? map[1]
})

const componentHealth = computed(() => {
  const comps = statusPage.value?.components ?? []
  const unhealthy = [2, 3, 4, 5]
  const healthy = comps.filter((c: any) => !unhealthy.includes(c.status)).length
  return { healthy, total: comps.length }
})

const averageUptime = computed(() => {
  if (!historyData.value?.data?.length) return null
  const sum = historyData.value.data.reduce((acc: number, point: any) => acc + (point.overallUptime ?? 0), 0)
  return (sum / historyData.value.data.length) * 100
})

const overallHistory = computed(() => {
  if (!historyData.value?.data?.length) return []
  return [...historyData.value.data]
    .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map((point: any) => ({
      date: point.date,
      uptime: point.overallUptime ?? 0,
      incidents: point.incidents?.length ?? 0
    }))
})

const componentHistoryMap = computed(() => {
  const map = new Map<string, Array<{ date: string; uptime: number; incidents: number }>>()
  if (!historyData.value?.data?.length) return map

  for (const point of historyData.value.data) {
    for (const comp of point.components ?? []) {
      const arr = map.get(comp.componentId) ?? []
      arr.push({
        date: point.date,
        uptime: comp.uptime ?? 0,
        incidents: comp.incidents ?? 0
      })
      map.set(comp.componentId, arr)
    }
  }

  for (const [key, arr] of map.entries()) {
    arr.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    map.set(key, arr)
  }

  return map
})

const historyRangeLabel = computed(() => {
  const data = historyData.value?.data
  if (!data || data.length === 0) return { start: '—', end: '—' }
  const sorted = [...data].sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
  return {
    start: formatDate(sorted[0].date),
    end: formatDate(sorted[sorted.length - 1].date)
  }
})

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function componentStatusClass(status: number) {
  switch (status) {
    case 1:
      return 'bg-emerald-100 text-emerald-800'
    case 2:
      return 'bg-amber-100 text-amber-800'
    case 3:
      return 'bg-orange-100 text-orange-800'
    case 4:
      return 'bg-red-100 text-red-800'
    case 5:
      return 'bg-indigo-100 text-indigo-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

function componentStatusDot(status: number) {
  switch (status) {
    case 1:
      return 'bg-emerald-500'
    case 2:
      return 'bg-amber-400'
    case 3:
      return 'bg-orange-500'
    case 4:
      return 'bg-red-500'
    case 5:
      return 'bg-indigo-500'
    default:
      return 'bg-gray-400'
  }
}

function componentStatusText(status: number) {
  switch (status) {
    case 1:
      return 'Operational'
    case 2:
      return 'Degraded'
    case 3:
      return 'Partial outage'
    case 4:
      return 'Major outage'
    case 5:
      return 'Maintenance'
    default:
      return 'Unknown'
  }
}

function historyBarClass(uptime: number, incidents: number) {
  if (incidents > 0 || uptime < 0.95) return 'bg-red-500'
  if (uptime < 0.985) return 'bg-orange-400'
  if (uptime < 0.995) return 'bg-amber-400'
  if (uptime < 0.999) return 'bg-lime-500'
  return 'bg-emerald-500'
}

function getComponentHistory(componentId: string) {
  const historyEntries = componentHistoryMap.value.get(componentId)
  if (historyEntries && historyEntries.length > 0) {
    return historyEntries
  }

  return Array.from({ length: 7 }).map((_, idx) => ({
    date: new Date(Date.now() - (6 - idx) * 24 * 60 * 60 * 1000).toISOString(),
    uptime: 1,
    incidents: 0
  }))
}

function incidentSeverityDot(severity: string | number) {
  const sev = typeof severity === 'string' ? severity.toLowerCase() : severity
  if (sev === 3 || sev === 'critical') return 'bg-red-500'
  if (sev === 2 || sev === 'major') return 'bg-orange-500'
  if (sev === 1 || sev === 'minor') return 'bg-amber-400'
  return 'bg-gray-400'
}

function formatStatusLabel(status: unknown) {
  if (status === null || status === undefined) return ''
  const label = typeof status === 'string' ? status : String(status)
  return label.replace(/_/g, ' ')
}

async function subscribe() {
  if (!subscriptionEmail.value || !statusPage.value?.slug) return

  subscribing.value = true
  try {
    const apiBase = resolveApiBase()

    // Simple pacing: ensure at least 800ms since page load to reduce bot noise
    const elapsed = Date.now() - pageLoadStartedAt
    if (elapsed < 800) {
      await new Promise(resolve => setTimeout(resolve, 800 - elapsed))
    }

    // Exchange page session token for subscription token (one-time/short-lived)
    if (!subscriptionToken.value) {
      const tokenUrl = `${apiBase}/public/status/${statusPage.value.slug}/subscription-token`
      const tokenResponse = await fetch(tokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pageSessionToken: pageSessionToken.value,
          honeypot: honeypot.value
        })
      })
      if (!tokenResponse.ok) {
        throw new Error('Unable to start subscription. Please reload and try again.')
      }
      const tokenBody = await tokenResponse.json()
      subscriptionToken.value = tokenBody.subscriptionToken || null
    }

    const apiUrl = `${apiBase}/public/status/${statusPage.value.slug}/subscribe`
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(subscriptionToken.value ? { 'X-Status-Token': subscriptionToken.value } : {})
      },
      body: JSON.stringify({
        email: subscriptionEmail.value,
        honeypot: honeypot.value,
        subscriptionToken: subscriptionToken.value,
        components: [],
        preferences: {
          incidents: true,
          maintenances: true,
          emailNotifications: true,
          smsNotifications: false,
          components: []
        }
      })
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    subscriptionEmail.value = ''
    alert('Successfully subscribed!')
  } catch (err) {
    console.error('Subscription error:', err)
    alert('Failed to subscribe. Please try again.')
  } finally {
    subscribing.value = false
  }
}

async function loadHistory() {
  if (!statusPage.value?.slug) return
  historyLoading.value = true
  historyError.value = false
  try {
    const apiBase = resolveApiBase()
    const apiUrl = `${apiBase}/public/status/${statusPage.value.slug}/history?days=${historyDays}`
    const response = await fetch(apiUrl)
    if (response.ok) {
      historyData.value = await response.json()
    } else {
      historyError.value = true
    }
  } catch (err) {
    historyError.value = true
    console.error('Failed to load history', err)
  } finally {
    historyLoading.value = false
  }
}

async function loadPastIncidents(loadMore = false) {
  if (!statusPage.value?.slug || pastIncidentsLoading.value) return
  pastIncidentsLoading.value = true
  try {
    const page = loadMore ? pastIncidentsPage.value + 1 : 1
    const apiBase = resolveApiBase()
    const apiUrl = `${apiBase}/public/status/${statusPage.value.slug}/incidents?days=${historyDays}&page=${page}&pageSize=10`
    const response = await fetch(apiUrl)
    if (!response.ok) throw new Error('Failed to fetch incidents')
    const incidents = await response.json()
    if (loadMore) {
      pastIncidentsPage.value = page
      pastIncidents.value.push(...incidents)
    } else {
      pastIncidentsPage.value = 1
      pastIncidents.value = incidents
    }
    pastIncidentsHasMore.value = incidents.length === 10
  } catch (err) {
    console.error('Failed to load past incidents:', err)
    pastIncidentsHasMore.value = false
  } finally {
    pastIncidentsLoading.value = false
  }
}

function loadMorePastIncidents() {
  if (!pastIncidentsHasMore.value || pastIncidentsLoading.value) return
  loadPastIncidents(true)
}

function resolveApiBase(): string {
  const envBase = (import.meta as any).env?.VITE_PUBLIC_STATUS_API_BASE as string | undefined
  return (envBase ? envBase.replace(/\/$/, '') : 'http://localhost:5010/api')
}

function buildStatusUrl(passwordParam?: string) {
  const apiBase = resolveApiBase()
  let apiUrl = `${apiBase}/public/status`

  const host = window.location.hostname
  if (host !== 'localhost' && host !== '127.0.0.1' && !host.includes('3001')) {
    const parts = host.split('.')
    if (parts.length >= 3 && parts[1] === 'pingalert' && parts[2] === 'live') {
      apiUrl += `/subdomain/${parts[0]}`
    } else {
      apiUrl += '/by-domain'
    }
  } else if (route.params.slug) {
    apiUrl += `/${route.params.slug as string}`
  } else {
    apiUrl += '/demo-status'
  }

  if (passwordParam) {
    apiUrl += `?password=${encodeURIComponent(passwordParam)}`
  }

  return apiUrl
}

async function loadStatusPage(passwordParam?: string) {
  try {
    const apiUrl = buildStatusUrl(passwordParam)
    const response = await fetch(apiUrl)

    if (response.status === 401) {
      showPasswordPrompt.value = true
      return
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    const body = await response.json()
    pageSessionToken.value = body.pageSessionToken || null
    subscriptionToken.value = null
    statusPage.value = body
    document.title = `${statusPage.value.name} | Status`
    updateFavicon()
    await Promise.all([loadHistory(), loadPastIncidents()])
  } catch (err) {
    error.value = true
    console.error('Failed to load status page:', err)
  } finally {
    loading.value = false
  }
}

async function authenticate() {
  if (!password.value) return
  passwordError.value = ''
  authenticating.value = true
  try {
    await loadStatusPage(password.value)
    if (statusPage.value) {
      showPasswordPrompt.value = false
      password.value = ''
    } else {
      passwordError.value = 'Invalid password'
    }
  } catch (err) {
    passwordError.value = 'Authentication failed'
    console.error('Authentication error:', err)
  } finally {
    authenticating.value = false
  }
}

function scrollToSubscribe() {
  subscriptionSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function updateFavicon() {
  if (statusPage.value?.branding?.faviconUrl) {
    const favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement
    if (favicon) favicon.href = statusPage.value.branding.faviconUrl
  }
}

onMounted(() => {
  loadStatusPage()
})
</script>

<style scoped>
:global(body) {
  background-color: #ffffff;
}

.prose :where(img) {
  display: inline-block;
}
</style>
